export const addAmoyNetwork = [
  {
    title: "Cómo agregar la red de prueba AMOY a MetaMask (guía paso a paso y detallada)",
    value: `
Agregar la red de prueba AMOY a MetaMask es esencial para interactuar con contratos y tokens en esta testnet de Polygon.
A continuación te explico cada paso en detalle:

---

1. **Abre MetaMask en tu navegador**  
   Haz clic en el ícono de la extensión MetaMask (el zorro naranja) en la barra de herramientas de tu navegador.  
   Si es necesario, ingresa tu contraseña para desbloquear la wallet.  
   > Tip: Si no ves MetaMask, revisa el menú de extensiones (ícono de rompecabezas).

   ![Paso 1](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-1.png)

2. **Accede al menú de redes**  
   En la parte superior de MetaMask, haz clic sobre el botón que muestra el nombre de la red actual (por ejemplo, “Ethereum Mainnet”, “Polygon Mainnet” o “Sepolia”) para abrir el menú de selección de red.

   ![Paso 2](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-1.png)

3. **Selecciona “Agregar una red personalizada”**  
   Desplázate hasta el final de la lista y haz clic en la opción "Agregar una red personalizada" o "Agregar red".

   ![Paso 3](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-15.png)

4. **Se abrirá el formulario para una nueva red**  
   Verás un formulario en MetaMask donde deberás ingresar la información correspondiente a la nueva red.  
   Este formulario te solicitará los siguientes campos:

   - El nombre de la red (para que puedas identificarla fácilmente).
   - La URL RPC, que es la dirección que usará MetaMask para conectarse a la red.
   - El ID de cadena, que identifica la red específica.
   - El símbolo de la moneda, para mostrar el saldo con el token correcto.
   - El explorador de bloques, que te permitirá ver tus transacciones y contratos en un sitio web externo.

   ![Paso 4](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-3.PNG)

5. **Consulta los datos oficiales de la red AMOY en la página de Polygon**  
   Antes de ingresar los datos en MetaMask, abre la página oficial de soporte de Polygon donde se listan todos los parámetros necesarios para agregar la red AMOY.  
   En este recurso verás claramente los campos y valores exactos que debes copiar:

   - **Network Name:** Polygon Amoy Testnet  
   - **New RPC URL:** https://rpc-amoy.polygon.technology/  
   - **Chain ID:** 80002  
   - **Currency Symbol:** POL  
   - **Block Explorer URL:** https://amoy.polygonscan.com/  

   Puedes consultar directamente la documentación aquí:  
   [Polygon Amoy Testnet - Support Page](https://polygon.technology/blog/introducing-the-amoy-testnet-for-polygon-pos)

   La imagen muestra la sección de la base de conocimientos de Polygon, donde aparecen todos estos datos bajo el título **"How to add Amoy Network"**. Este recurso te garantiza que estás usando los parámetros oficiales y actualizados.

   ![Consulta los datos de la red AMOY](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-4.png)


6. **Ingresa el nombre de la red**  
   En el campo **Nombre de la red**, escribe el nombre que te ayudará a identificar esta red dentro de MetaMask.  
   Se recomienda usar el nombre oficial para evitar confusiones, por ejemplo:  
   **Polygon Amoy Testnet**  
   Este nombre es solo una etiqueta para tu referencia personal y puedes modificarlo si lo prefieres, pero se sugiere usar el nombre oficial para reconocer fácilmente la testnet de Polygon cuando tengas varias redes configuradas en tu wallet.

   ![Campo nombre de la red](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-5.png)

7. **Agrega la URL RPC manualmente**  
   En el campo **Nueva URL RPC**, escribe exactamente la dirección:  
   https://rpc-amoy.polygon.technology  
   La URL RPC es la puerta de acceso que MetaMask utilizará para conectarse con la red de prueba Amoy.  
   Asegúrate de copiarla sin errores, ya que cualquier diferencia puede impedir la conexión a la red.  
   En algunas versiones de MetaMask, puede aparecer un botón o sección para agregar la URL RPC manualmente. Si es así, simplemente pega la dirección ahí.

   ![Campo URL RPC](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-6.png)


8. **Ingresa el ID de cadena**  
   En el campo **ID de cadena**, escribe: 80002  
   El ID de cadena, también conocido como "Chain ID", es un número único que sirve para identificar cada red blockchain.  
   Al ingresar el ID 80002, le indicas a MetaMask que deseas conectarte específicamente a la red de prueba Amoy de Polygon, evitando confusiones o errores al interactuar con diferentes redes.

   ![Campo ID de cadena](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-7.png)

9. **Especifica el símbolo de la moneda**  
   En el campo **Símbolo de moneda**, escribe: POL  
   Este campo le indica a MetaMask qué abreviatura mostrar para el token principal de la red.  
   Anteriormente, en las redes de prueba y en la red principal de Polygon, el símbolo era **MATIC**. Sin embargo, a partir de la testnet Amoy, el nuevo token nativo de Polygon se llama **POL**.  
   POL representa la evolución del ecosistema Polygon y será el nuevo estándar en todas las redes de Polygon en el futuro.  
   Por eso, es importante escribir **POL** y no MATIC, para que tu wallet muestre correctamente tu saldo en la red Amoy.

   ![Campo símbolo de moneda](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-8a.png)

10. **Agrega la URL del explorador de bloques**  
    Haz clic en el botón para añadir una URL del explorador de bloques y se abrirá una ventana adicional donde podrás escribir la dirección.  
    Ingresa: https://amoy.polygonscan.com y luego presiona **Agregar URL**.  
    Esta URL te permitirá acceder a la página oficial donde puedes consultar todas las transacciones y contratos en la red de prueba Amoy.  
    La imagen muestra exactamente la ventana emergente donde debes pegar la dirección antes de confirmar.

    ![Agregar URL del explorador de bloques](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-9.png)

11. **Agrega y confirma la URL del explorador de bloques**  
    En esta ventana emergente de MetaMask, deberás ingresar la dirección oficial del explorador de bloques para la red Amoy:  
    https://amoy.polygonscan.com  
    Una vez que la escribas o pegues en el campo correspondiente, haz clic en el botón azul **"Agregar URL"** para confirmar.

    Esta acción vincula tu red personalizada con el explorador oficial, permitiéndote visualizar y verificar todas las transacciones y actividades en la testnet Amoy directamente desde MetaMask.

    ![Agregar URL del explorador de bloques](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-10.png)








12. **Revisa y confirma todos los datos**  
    Antes de guardar, verifica que todos los campos estén completos y sean correctos:  
    - Nombre de la red: Amoy  
    - URL RPC: https://rpc-amoy.polygon.technology  
    - ID de cadena: 80002  
    - Símbolo: POL  
    - Explorador: https://amoy.polygonscan.com  
    Revisa cuidadosamente, un error puede impedir que la red funcione.

    ![Paso 10](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-11.png)

11. **Guarda la configuración**  
    Haz clic en Guardar para añadir la nueva red.  
    MetaMask procesará la información y agregará Amoy a tu lista de redes.

    ![Paso 11](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-11.png)



12. **Confirma el mensaje de éxito**  
    Al guardar la configuración, MetaMask te mostrará una notificación indicando que la red Amoy se añadió correctamente.  
    Este mensaje es la confirmación visual de que la red fue registrada sin errores y ya está disponible para su uso.  
    Si ves este aviso, significa que todos los campos fueron llenados correctamente y puedes continuar.

    ![Mensaje de éxito al agregar la red Amoy](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-12.png)

13. **Verifica que Amoy esté en la lista de redes**  
    Abre nuevamente el menú desplegable de redes en MetaMask y busca la opción **Amoy**.  
    Debe aparecer junto a otras redes que tengas configuradas.  
    Si la ves en la lista, significa que ya puedes seleccionarla y comenzar a operar en la testnet Amoy.

    ![Verificar Amoy en la lista de redes](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-13.png)

14. **Usa la red Amoy en MetaMask**  
    Finalmente, selecciona la red Amoy desde el menú de redes.  
    Ahora puedes realizar pruebas, recibir tokens POL de faucets, y desplegar contratos en esta testnet sin riesgo alguno.  
    Recuerda: Los fondos y tokens en la red Amoy son solo para pruebas y aprendizaje, no tienen valor real ni pueden transferirse a la red principal de Polygon.

    ![Seleccionar y usar la red Amoy en MetaMask](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/8-14.png)

---
`
  }
];

export default addAmoyNetwork;
